import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ExternalLink, 
  Bitcoin, 
  RefreshCw, 
  TrendingUp, 
  DollarSign, 
  Package, 
  Target,
  Building2,
  AlertCircle
} from "lucide-react";
import { useSecFilings, useBtcAcquisitions, SecFiling } from "@/hooks/useSecFilings";
import { formatUSD, formatNum } from "@/lib/format";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const TICKER_COLORS: Record<string, string> = {
  'MSTR': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  'COIN': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'MARA': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  'RIOT': 'bg-green-500/20 text-green-400 border-green-500/30',
  'CLSK': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  'HUT': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
};

function getConfidenceBadge(confidence: string | null) {
  if (!confidence) return null;
  const colors: Record<string, string> = {
    high: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    low: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  return (
    <Badge variant="outline" className={`text-[10px] ${colors[confidence] || ''}`}>
      {confidence}
    </Badge>
  );
}

function AcquisitionCard({ filing }: { filing: SecFiling }) {
  const hasDetailedData = filing.extracted_total_usd || filing.extracted_avg_price || filing.extracted_total_holdings;
  
  return (
    <div className="p-4 rounded-lg bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-500/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Badge 
            variant="outline" 
            className={`font-mono text-xs ${TICKER_COLORS[filing.ticker] || ''}`}
          >
            {filing.ticker}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {new Date(filing.filing_date).toLocaleDateString('pt-BR')}
          </span>
          {getConfidenceBadge(filing.parsing_confidence)}
        </div>
        {filing.link && (
          <a 
            href={filing.link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-muted-foreground hover:text-primary transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        {filing.extracted_btc_amount && (
          <div className="flex items-center gap-2">
            <Bitcoin className="h-4 w-4 text-orange-400" />
            <div>
              <p className="text-xs text-muted-foreground">BTC Comprados</p>
              <p className="font-semibold text-orange-400">
                {formatNum(filing.extracted_btc_amount, 0)} BTC
              </p>
            </div>
          </div>
        )}

        {filing.extracted_total_usd && (
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-400" />
            <div>
              <p className="text-xs text-muted-foreground">Valor Total</p>
              <p className="font-semibold text-emerald-400">
                {formatUSD(filing.extracted_total_usd)}
              </p>
            </div>
          </div>
        )}

        {filing.extracted_avg_price && (
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-blue-400" />
            <div>
              <p className="text-xs text-muted-foreground">Preço Médio</p>
              <p className="font-semibold text-blue-400">
                {formatUSD(filing.extracted_avg_price)}
              </p>
            </div>
          </div>
        )}

        {filing.extracted_total_holdings && (
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-purple-400" />
            <div>
              <p className="text-xs text-muted-foreground">Holdings Total</p>
              <p className="font-semibold text-purple-400">
                {formatNum(filing.extracted_total_holdings, 0)} BTC
              </p>
            </div>
          </div>
        )}
      </div>

      {!hasDetailedData && filing.extracted_btc_amount && (
        <p className="text-xs text-muted-foreground mt-2 italic">
          Dados detalhados não disponíveis neste filing
        </p>
      )}
    </div>
  );
}

function FilingRow({ filing }: { filing: SecFiling }) {
  return (
    <a
      href={filing.link || '#'}
      target="_blank"
      rel="noopener noreferrer"
      className="block p-3 rounded-lg bg-card/50 hover:bg-card/80 border border-border/50 transition-all hover:border-primary/30 group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge 
              variant="outline" 
              className={TICKER_COLORS[filing.ticker] || 'bg-muted'}
            >
              {filing.ticker}
            </Badge>
            <Badge variant="secondary" className="text-xs">
              {filing.filing_type}
            </Badge>
            {filing.is_bitcoin_related && (
              <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                <Bitcoin className="h-3 w-3 mr-1" />
                BTC
              </Badge>
            )}
          </div>
          <p className="text-sm text-foreground/80 line-clamp-2">
            {filing.title || 'SEC Filing'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {formatDistanceToNow(new Date(filing.filing_date), { 
              addSuffix: true, 
              locale: ptBR 
            })}
          </p>
        </div>
        <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
      </div>
      {filing.extracted_btc_amount && !filing.extracted_total_usd && (
        <div className="mt-2 flex items-center gap-1 text-amber-400">
          <Bitcoin className="h-4 w-4" />
          <span className="text-sm font-medium">
            {formatNum(filing.extracted_btc_amount, 0)} BTC
          </span>
        </div>
      )}
    </a>
  );
}

export function CorporateBTCMonitor() {
  const { data: filings, isLoading: filingsLoading, refetch: refetchFilings, isFetching } = useSecFilings(30);
  const { data: acquisitions, isLoading: acquisitionsLoading, refetch: refetchAcquisitions } = useBtcAcquisitions(5);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const { error } = await supabase.functions.invoke('sec-edgar-filings');
      if (error) throw error;
      
      await Promise.all([refetchFilings(), refetchAcquisitions()]);
      toast.success('SEC filings atualizados com sucesso');
    } catch (err) {
      console.error('Refresh error:', err);
      toast.error('Erro ao atualizar filings');
    } finally {
      setIsRefreshing(false);
    }
  };

  const isLoading = filingsLoading || acquisitionsLoading;
  const btcAcquisitions = acquisitions?.filter(f => f.extracted_btc_amount) || [];
  const recentFilings = filings?.filter(f => !btcAcquisitions.find(a => a.id === f.id)).slice(0, 15) || [];

  return (
    <Card className="col-span-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Corporate BTC Monitor</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              SEC EDGAR
            </Badge>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing || isFetching}
              className="h-8 w-8"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing || isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          SEC 8-K Filings de MSTR, COIN, MARA, RIOT, CLSK, HUT
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : (
          <>
            {/* BTC Acquisitions Section */}
            {btcAcquisitions.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-orange-400" />
                  <h3 className="text-sm font-semibold">Aquisições de BTC Detectadas</h3>
                  <Badge variant="secondary" className="text-xs">
                    {btcAcquisitions.length}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {btcAcquisitions.map(filing => (
                    <AcquisitionCard key={filing.id} filing={filing} />
                  ))}
                </div>
              </div>
            )}

            {/* All Filings Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">Outros Filings Recentes</h3>
              <ScrollArea className="h-[350px] pr-4">
                {recentFilings.length === 0 && btcAcquisitions.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <AlertCircle className="h-8 w-8 mb-2" />
                    <p className="text-sm">Nenhum filing encontrado</p>
                    <p className="text-xs mt-1">Clique em refresh para buscar dados</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {recentFilings.map(filing => (
                      <FilingRow key={filing.id} filing={filing} />
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
